// Configuration fetched from the backend
export interface AppConfiguration {
  slideDuration: {
    default: number;
    min: number;
    max: number;
  };
  scrollSpeed: {
    default: number;
    min: number;
    max: number;
  };
  displayMode: string;
  autoStart: boolean;
  theme: {
    fontScale: number;
    primaryColor: string;
    backgroundStartColor: string;
    backgroundEndColor: string;
  };
}

// Default configuration (used if backend is unreachable)
const DEFAULT_CONFIG: AppConfiguration = {
  slideDuration: {
    default: 25000,
    min: 5000,
    max: 120000,
  },
  scrollSpeed: {
    default: 30,
    min: 10,
    max: 100,
  },
  displayMode: 'scrolling',
  autoStart: false,
  theme: {
    fontScale: 1.0,
    primaryColor: '#2196f3',
    backgroundStartColor: '#1a1a2e',
    backgroundEndColor: '#16213e',
  },
};

let cachedConfig: AppConfiguration | null = null;
let fetchPromise: Promise<AppConfiguration> | null = null;

/**
 * Fetch configuration from the backend with caching and retry logic.
 * Returns default configuration if backend is unavailable.
 */
export async function fetchConfiguration(): Promise<AppConfiguration> {
  // Return cached config if available
  if (cachedConfig) {
    return cachedConfig;
  }

  // Return existing fetch promise if one is in progress
  if (fetchPromise) {
    return fetchPromise;
  }

  // Start new fetch
  fetchPromise = (async () => {
    let lastError: Error | null = null;

    // Try up to 3 times with exponential backoff
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const response = await fetch('/api/configuration', {
          signal: AbortSignal.timeout(5000), // 5 second timeout
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const config = await response.json();
        cachedConfig = config;
        console.log('Configuration loaded from backend:', config);
        return config;
      } catch (error) {
        lastError = error as Error;
        console.warn(
          `Configuration fetch attempt ${attempt}/3 failed:`,
          error
        );

        if (attempt < 3) {
          // Exponential backoff: 1s, 2s
          await new Promise((resolve) =>
            setTimeout(resolve, attempt * 1000)
          );
        }
      }
    }

    // All attempts failed - use default configuration
    console.warn(
      'Failed to fetch configuration after 3 attempts. Using defaults.',
      lastError
    );
    cachedConfig = DEFAULT_CONFIG;
    return DEFAULT_CONFIG;
  })();

  try {
    return await fetchPromise;
  } finally {
    fetchPromise = null;
  }
}

/**
 * Apply theme configuration to the document root CSS variables.
 */
export function applyTheme(theme: AppConfiguration['theme']): void {
  const root = document.documentElement;
  root.style.setProperty('--theme-font-scale', theme.fontScale.toString());
  root.style.setProperty('--theme-primary-color', theme.primaryColor);
  root.style.setProperty(
    '--theme-background-start',
    theme.backgroundStartColor
  );
  root.style.setProperty('--theme-background-end', theme.backgroundEndColor);
}

/**
 * Reset cached configuration (useful for testing or after errors).
 */
export function resetConfiguration(): void {
  cachedConfig = null;
  fetchPromise = null;
}
